import { FC, ReactNode, useEffect, useState } from 'react';

import { ApplicationProvider, I18n } from '@eneco/common/application';
import { LinkComponentProvider } from '@eneco/link';
import { Head, errorPaths, Link } from '@eneco/next';
import { OverloadGuard } from '@eneco/overload';
import { SparkyProvider } from '@eneco/sparky/providers';
import { TrackingProvider } from '@eneco/tracking';
import { client } from '@sitecore/client/browser';
import { SitecoreContext } from '@sitecore/common';
import type { ComponentsDefinition } from '@sitecore/common';
import type { LayoutServiceData } from '@sitecore/types/lib';

type Props = {
  businessUnit: string;
  children?: ReactNode;
  components: ComponentsDefinition;
  i18n: I18n;
  label: string;
  layoutData: LayoutServiceData | null;
  name: string;
};

const ErrorPageContainer: FC<Props> = ({ children, components, i18n, label, layoutData, name }) => {
  const [errorLayoutData, setErrorLayoutData] = useState<LayoutServiceData | null>(layoutData);

  useEffect(() => {
    client({ path: errorPaths.error, locale: i18n.locale }).then(({ layoutData }) => {
      setErrorLayoutData(layoutData);
    });
  }, [i18n.locale]);

  if (!errorLayoutData || !errorLayoutData.sitecore.route) return null;

  return (
    <>
      <Head>
        <title>{label}</title>
      </Head>
      <ApplicationProvider i18n={i18n}>
        <TrackingProvider scope={name}>
          <SparkyProvider locale={i18n.locale}>
            <SitecoreContext components={components} layoutData={errorLayoutData}>
              <OverloadGuard>
                <LinkComponentProvider component={Link}>{children}</LinkComponentProvider>
              </OverloadGuard>
            </SitecoreContext>
          </SparkyProvider>
        </TrackingProvider>
      </ApplicationProvider>
    </>
  );
};

export { ErrorPageContainer };
